import { useTranslation } from 'react-i18next';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { Medal, Trophy, Heart, Globe, CheckCircle2, ChevronRight, GraduationCap, Briefcase, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ScholarshipsPage() {
  const { t } = useTranslation();

  const scholarshipTypes = [
    { 
      key: 'excellence', 
      icon: <Medal className="w-8 h-8" />, 
      amount: 'Jusqu\'à 100%', 
      criteria: ['Moyenne générale ≥ 16/20', 'Excellent dossier académique', 'Engagement communautaire'],
      image: '/images/campus/ceremony-01.jpg'
    },
    { 
      key: 'merit', 
      icon: <Trophy className="w-8 h-8" />, 
      amount: '25-50%', 
      criteria: ['Moyenne générale ≥ 14/20', 'Projet entrepreneurial innovant', 'Participation vie ESTAD'],
      image: '/images/campus/activity-02.jpg'
    },
    { 
      key: 'need', 
      icon: <Heart className="w-8 h-8" />, 
      amount: 'Aide Sociale', 
      criteria: ['Besoins financiers prouvés', 'Entretien de motivation', 'Ressources familiales limitées'],
      image: '/images/campus/doc-gathering-01.jpg'
    },
    { 
      key: 'international', 
      icon: <Globe className="w-8 h-8" />, 
      amount: 'Mobilité', 
      criteria: ['Étudiant hors Cameroun', 'Niveau B2 (Fr/En)', 'Engagement retour pays'],
      image: '/images/campus/students-01.jpg'
    }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('scholarships_hero_title', 'Bourses & Aide Financière')}
        description="ESTAD s’engage à rendre l’excellence éducative accessible à tous les talents, sans distinction d’origine sociale. Nous soutenons nos étudiants les plus motivés."
        breadcrumbs={[
          { label: t('nav_admissions'), href: '/admissions' },
          { label: t('scholarships_hero_badge', 'Scholarships'), href: '/admissions/scholarships' }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Stats Bar Highlight */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-8 border-b-2 border-gray-100 pb-12">
            {[
              { val: '500M+', label: 'Budget annuel (XAF)' },
              { val: '300+', label: 'Boursiers par an' },
              { val: '4', label: 'Types de bourses' },
              { val: '100%', label: 'Couverture max' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center md:text-left space-y-1">
                <div className="text-3xl md:text-4xl font-black text-navy uppercase tracking-tighter leading-none">{stat.val}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gold">{stat.label}</div>
              </div>
            ))}
          </section>

          {/* Intro Section with Image */}
          <section className="flex flex-col md:flex-row gap-10 items-center">
            <div className="flex-1 space-y-8">
               <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic leading-tight">
                 Récompenser le Mérite, Soutenir l'Avenir
               </h2>
               <p className="text-gray-600 font-medium leading-relaxed text-lg">
                 Le programme de bourses d'ESTAD vise à identifier et à soutenir les futurs leaders technologiques de l'Afrique. Que ce soit pour l'excellence académique, le potentiel entrepreneurial ou le soutien social, nous avons un dispositif adapté à chaque situation.
               </p>
               <ul className="space-y-4">
                  {[
                    "Accompagnement financier transparent",
                    "Critères basés sur la performance et le besoin",
                    "Renouvellement possible sur toute la scolarité"
                  ].map((p, i) => (
                    <li key={i} className="flex items-center gap-3 text-navy font-bold text-sm">
                       <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                       {p}
                    </li>
                  ))}
               </ul>
            </div>
            <div className="w-full md:w-1/3 aspect-[3/4] overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold flex-shrink-0">
               <img src="/images/campus/ceremony-01.jpg" alt="Excellence" className="w-full h-full object-cover" />
            </div>
          </section>

          {/* Scholarship Types Grid */}
          <section id="types" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic flex items-center gap-4">
               <GraduationCap className="w-8 h-8 text-gold" /> {t('scholarship_types_title', 'Nos Dispositifs de Soutien')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {scholarshipTypes.map((s, idx) => (
                <div key={idx} className="bg-white border border-gray-100 hover:shadow-2xl hover:border-gold transition-all duration-500 rounded-sm group relative overflow-hidden flex flex-col h-full">
                  <div className="h-40 overflow-hidden relative">
                    <img src={s.image} alt={s.key} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-navy/20" />
                    <div className="absolute top-4 right-4">
                      <span className="bg-gold text-navy text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">{s.amount}</span>
                    </div>
                  </div>
                  <div className="p-8 flex-1 flex flex-col space-y-6">
                    <div className="w-14 h-14 rounded-xl bg-gray-50 flex items-center justify-center text-navy group-hover:bg-navy group-hover:text-gold transition-all">
                      {s.icon}
                    </div>
                    <h3 className="text-2xl font-black text-navy uppercase tracking-tight leading-none">{t(`scholarship_${s.key}_title`)}</h3>
                    <div className="space-y-3 flex-1">
                      {s.criteria.map((c, cidx) => (
                        <div key={cidx} className="flex items-center gap-3 text-sm font-medium text-gray-500">
                          <CheckCircle2 className="w-4 h-4 text-gold/40 flex-shrink-0" /> {c}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Process Timeline CTA */}
          <section id="process" className="bg-navy p-12 md:p-20 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-[2000ms] pointer-events-none"></div>
            <div className="relative z-10 space-y-12">
               <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic italic text-center leading-none">
                Comment Postuler ?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                 {[
                   { step: 1, title: 'Vérifier', desc: 'Consultez les critères d\'éligibilité pour chaque type de bourse.' },
                   { step: 2, title: 'Préparer', desc: 'Rassemblez vos relevés de notes et rédigez votre lettre de motivation.' },
                   { step: 3, title: 'Postuler', desc: 'Soumettez votre demande lors de votre pré-inscription en ligne.' },
                   { step: 4, title: 'Entretien', desc: 'Rencontrez le comité des bourses pour défendre votre projet.' }
                 ].map((s, idx) => (
                   <div key={idx} className="space-y-4 p-8 bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-center rounded-sm group/card">
                      <div className="w-12 h-12 rounded-full bg-gold text-navy flex items-center justify-center mx-auto font-black text-xl mb-4 group-hover/card:scale-110 transition-transform shadow-lg">{s.step}</div>
                      <h4 className="text-sm font-black uppercase tracking-widest text-gold">{s.title}</h4>
                      <p className="text-white/40 text-[10px] font-medium leading-relaxed">{s.desc}</p>
                   </div>
                 ))}
              </div>
              <div className="flex justify-center pt-8">
                 <Link to="/admissions" className="bg-gold text-navy px-16 py-5 font-black uppercase tracking-widest text-sm shadow-xl hover:brightness-110 transition-all active:scale-95 flex items-center gap-3">
                   Ouvrir un Dossier de Bourse <ChevronRight className="w-5 h-5" />
                 </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
